import { useEffect, useMemo, useRef, useState } from 'react';
import * as D from '@radix-ui/react-dialog';
import { Building2, CornerDownLeft, Home, Moon, Palette, Search, Sun } from 'lucide-react';
import type { MenuNode } from '@/lib/types';
import { cn } from '@/lib/cn';
import { flatten, leafIcon, matchScore } from './menu';
import { useWorkspace } from './workspace';
import { useTheme } from '@/theme/ThemeProvider';
import { useAuth } from '@/auth/AuthContext';

interface Cmd { id: string; title: string; hint?: string; group: string; icon: any; run: () => void; score: number }

/** Ctrl+K: menüdeki tüm ekranlar, açık sekmeler ve görünüm komutları */
export function CommandPalette({ open, onOpenChange, menu, onOpenNode, onAppearance }: {
  open: boolean; onOpenChange: (o: boolean) => void; menu: MenuNode[]; onOpenNode: (n: MenuNode) => void; onAppearance: () => void;
}) {
  const ws = useWorkspace();
  const theme = useTheme();
  const { user } = useAuth();
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  /** menü henüz yüklenmeden basılan Enter: sonuçlar gelince çalıştırılır */
  const [pending, setPending] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const items = useMemo(() => flatten(menu), [menu]);

  useEffect(() => { if (open) { setQ(''); setSel(0); setPending(false); } }, [open]);

  const cmds = useMemo<Cmd[]>(() => {
    const out: Cmd[] = [];
    const close = (f: () => void) => () => { onOpenChange(false); f(); };
    out.push({ id: 'home', title: 'Ana sayfa', group: 'Genel', icon: Home, run: close(() => ws.activate(null)), score: matchScore('Ana sayfa gösterge paneli', q) });
    for (const t of ws.tabs) out.push({ id: 'tab:' + t.id, title: t.title, hint: 'açık sekme', group: 'Açık sekmeler', icon: CornerDownLeft, run: close(() => ws.activate(t.id)), score: matchScore(t.title, q) });
    for (const it of items) {
      const s = Math.max(matchScore(it.node.title, q), matchScore([...it.path, it.node.title].join(' '), q) * 0.8);
      out.push({ id: it.node.key, title: it.node.title, hint: it.path.join(' › '), group: 'Ekranlar', icon: leafIcon(it.node.kind), run: close(() => onOpenNode(it.node)), score: s });
    }
    if (user?.flags?.Yonetici) out.push({ id: 'sirketler', title: 'Şirket tanımları', hint: 'veritabanı bağlantıları', group: 'Yönetim', icon: Building2, run: close(() => ws.open({ kind: 'custom', component: 'SirketTanimlari', title: 'Şirket tanımları', props: { persist: true } })), score: matchScore('şirket tanımları veritabanı bağlantı', q) });
    out.push({ id: 'gorunum', title: 'Görünüm ve tasarım seç…', group: 'Görünüm', icon: Palette, run: close(onAppearance), score: matchScore('görünüm tasarım tema renk', q) });
    out.push({ id: 'mod', title: theme.dark ? 'Açık moda geç' : 'Koyu moda geç', group: 'Görünüm', icon: theme.dark ? Sun : Moon, run: close(() => theme.save({ mod: theme.dark ? 'acik' : 'koyu' })), score: matchScore('koyu açık mod tema', q) });
    return out.filter((c) => c.score > 0).sort((a, b) => (q ? b.score - a.score : 0)).slice(0, q ? 60 : 40);
  }, [items, ws.tabs, q, theme.dark, user]);

  useEffect(() => {
    listRef.current?.querySelector(`[data-idx="${sel}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [sel]);

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(cmds.length - 1, s + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(0, s - 1)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      const c = cmds[Math.min(sel, cmds.length - 1)] ?? cmds[0];
      if (c && (c.id !== 'home' || items.length)) c.run(); else if (q.trim()) setPending(true);
    }
  };
  useEffect(() => {
    if (pending && items.length && cmds.length) { setPending(false); cmds[0].run(); }
  }, [pending, items.length, cmds]);

  let lastGroup = '';
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-[2px]" />
        <D.Content className="cmdk fixed left-1/2 top-[14vh] z-[71] flex max-h-[70vh] w-[min(640px,calc(100vw-24px))] -translate-x-1/2 flex-col overflow-hidden" onKeyDown={onKey}>
          <D.Title className="sr-only">Komut paleti</D.Title>
          <D.Description className="sr-only">Ekran, sekme veya komut arayın</D.Description>
          <div className="flex items-center gap-3 border-b b-border px-4">
            <Search size={17} className="muted" />
            <input autoFocus value={q} onChange={(e) => { setQ(e.target.value); setSel(0); }} placeholder="Ekran, rapor ya da komut ara…" className="h-13 flex-1 bg-transparent text-[15px] outline-none placeholder:text-[var(--muted)]" />
            <span className="kbd">ESC</span>
          </div>
          <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto p-2 scroll-thin">
            {cmds.length === 0 && <div className="p-8 text-center muted">{items.length ? 'Sonuç yok' : 'Menü yükleniyor…'}</div>}
            {cmds.map((c, i) => {
              const head = !q && c.group !== lastGroup ? (lastGroup = c.group) : null;
              const Icon = c.icon;
              return (
                <div key={c.id}>
                  {head && <div className="h-label px-2 pb-1 pt-3">{head}</div>}
                  <button data-idx={i} onMouseMove={() => setSel(i)} onClick={c.run}
                    className={cn('cmdk-item flex w-full items-center gap-3 px-2.5 py-2 text-left', i === Math.min(sel, cmds.length - 1) && 'is-sel')}>
                    <Icon size={16} className="shrink-0 muted" />
                    <span className="min-w-0 flex-1 truncate">{c.title}</span>
                    {c.hint && <span className="max-w-[45%] truncate text-xs muted">{c.hint}</span>}
                    {i === Math.min(sel, cmds.length - 1) && <CornerDownLeft size={14} className="muted" />}
                  </button>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-3 border-t b-border px-4 py-2 text-[11px] muted">
            <span><span className="kbd">↑</span> <span className="kbd">↓</span> gezin</span><span><span className="kbd">↵</span> aç</span><span className="ml-auto">{items.length} ekran</span>
          </div>
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}
